import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Input, Alert } from '../ui';
import { FiLock, FiCheck } from 'react-icons/fi';
import { supabase } from '../../config/supabase';

const supabaseConfigured = Boolean(
  process.env.REACT_APP_SUPABASE_URL && process.env.REACT_APP_SUPABASE_ANON_KEY
);

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [sessionReady, setSessionReady] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const navigate = useNavigate();

  const baseURL =
    process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api';

  useEffect(() => {
    let cancelled = false;

    const stripRecoveryParamsFromUrl = () => {
      const path = window.location.pathname;
      window.history.replaceState(null, '', path);
    };

    const establishSession = async () => {
      setError('');
      if (!supabaseConfigured) {
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const token = hashParams.get('access_token');
        if (token) {
          setAccessToken(token);
          setSessionReady(true);
        } else {
          setError(
            'Invalid or missing reset link. Please request a new password reset from the login page.'
          );
        }
        return;
      }

      const url = new URL(window.location.href);
      const code = url.searchParams.get('code');
      const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      const access_token = hashParams.get('access_token');
      const refresh_token = hashParams.get('refresh_token');

      try {
        if (code) {
          const { error: exErr } = await supabase.auth.exchangeCodeForSession(
            window.location.href
          );
          if (exErr) throw exErr;
          if (!cancelled) {
            setSessionReady(true);
            stripRecoveryParamsFromUrl();
          }
          return;
        }

        if (access_token && refresh_token) {
          const { error: sessErr } = await supabase.auth.setSession({
            access_token,
            refresh_token
          });
          if (sessErr) throw sessErr;
          if (!cancelled) {
            setAccessToken(access_token);
            setSessionReady(true);
            stripRecoveryParamsFromUrl();
          }
          return;
        }

        const {
          data: { session },
          error: sessionErr
        } = await supabase.auth.getSession();
        if (sessionErr) throw sessionErr;
        if (session?.user && !cancelled) {
          setSessionReady(true);
          return;
        }

        if (!cancelled) {
          setError(
            'This reset link is invalid or has expired. Please use “Forgot password” on the login page to get a new code or link.'
          );
        }
      } catch (e) {
        if (!cancelled) {
          setError(
            e?.message ||
              'Token has expired or is invalid. Please request a new password reset.'
          );
        }
      }
    };

    establishSession();
    return () => {
      cancelled = true;
    };
  }, []);

  const checkPasswordStrength = (pass) => {
    let strength = 0;
    if (pass.length >= 8) strength += 1;
    if (/[A-Z]/.test(pass)) strength += 1;
    if (/[0-9]/.test(pass)) strength += 1;
    if (/[^A-Za-z0-9]/.test(pass)) strength += 1;
    setPasswordStrength(strength);
  };

  const handlePasswordChange = (e) => {
    const newPass = e.target.value;
    setPassword(newPass);
    checkPasswordStrength(newPass);
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (!sessionReady) {
      setError('Missing or expired reset session. Please open the link from your email again.');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      if (supabaseConfigured) {
        // Persists to Supabase Auth (same store used by email/password sign-in)
        const { error: updErr } = await supabase.auth.updateUser({ password });
        if (updErr) throw updErr;
        await supabase.auth.signOut();
        setMessage('Password has been reset. Redirecting to login…');
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      if (!accessToken) {
        setError('Missing access token. Please request a new reset link.');
        return;
      }

      const response = await axios.post(`${baseURL}/auth/reset-password`, {
        newPassword: password,
        accessToken: accessToken,
        type: 'reset'
      });

      setMessage(response.data.message);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      console.error('Password reset error:', err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'An error occurred while resetting your password. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = sessionReady;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <Card className="max-w-md w-full" shadow="lg" padding="none">
        <Card.Content className="p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
              <FiLock className="w-8 h-8 text-amber-600 dark:text-amber-400" />
            </div>
            <h1 className="text-2xl font-serif text-primary-900 dark:text-white mb-2">
              Set New Password
            </h1>
            <p className="font-literary text-primary-600 dark:text-gray-300">
              Please enter your new password below
            </p>
          </div>

          {message && (
            <div className="mb-6">
              <Alert type="success">{message}</Alert>
            </div>
          )}

          {error && (
            <div className="mb-6">
              <Alert type="error" onClose={() => setError('')}>
                {error}
              </Alert>
            </div>
          )}

          {!message && (
            <form onSubmit={handlePasswordReset} className="space-y-6">
              <div>
                <Input
                  label="New Password"
                  type="password"
                  icon={FiLock}
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder="Enter new password"
                  required
                  disabled={loading}
                />
                {password && (
                  <div className="mt-2 flex space-x-1">
                    {[1, 2, 3, 4].map((level) => (
                      <div
                        key={level}
                        className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
                          passwordStrength >= level
                            ? passwordStrength <= 2
                              ? 'bg-red-400'
                              : passwordStrength === 3
                                ? 'bg-amber-400'
                                : 'bg-green-400'
                            : 'bg-gray-200 dark:bg-gray-700'
                        }`}
                      />
                    ))}
                  </div>
                )}
                {password && (
                  <p className="text-xs text-right mt-1 text-gray-500 dark:text-gray-400">
                    Strength:{' '}
                    {['Weak', 'Fair', 'Good', 'Strong'][passwordStrength - 1] || 'Weak'}
                  </p>
                )}
              </div>

              <Input
                label="Confirm Password"
                type="password"
                icon={FiCheck}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
                disabled={loading}
              />

              <Button
                type="submit"
                variant="primary"
                className="w-full bg-amber-600 hover:bg-amber-700 text-white border-transparent"
                loading={loading}
                disabled={loading || !canSubmit}
              >
                {loading ? 'Resetting Password...' : 'Reset Password'}
              </Button>
            </form>
          )}
        </Card.Content>
      </Card>
    </div>
  );
};

export default ResetPassword;
